import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail, formatMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import CredentialsModal from "@/components/CredentialsModal";
import { Plus, Pencil, Trash2, Search, Anchor, Users } from "lucide-react";


const empty = {
  full_name: "", email: "", phone: "", designation: "", department: "Operations",
  employee_type: "onshore", join_date: new Date().toISOString().slice(0, 10),
  salary: 0, status: "active", avatar_url: "", send_email: false,
};

export default function Employees() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [creds, setCreds] = useState(null);

  // refs here
  const emailRef = useRef(null);
  const phoneRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/employees");
      setList(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (emp) => {
    setEditing(emp.id);
    setForm({
      full_name: emp.full_name,
      email: emp.email,
      phone: emp.phone || "",
      designation: emp.designation,
      department: emp.department,
      employee_type: emp.employee_type,
      join_date: emp.join_date,
      salary: emp.salary || 0,
      status: emp.status,
      avatar_url: emp.avatar_url || "",
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, salary: Number(form.salary) || 0 };
      if (editing) {
        const { send_email, email, ...editPayload } = payload;
        await api.put(`/employees/${editing}`, editPayload);
        toast.success("Employee updated");
      } else {
        const { data } = await api.post("/employees", payload);
        toast.success("Employee added");
        if (data.credentials) setCreds(data.credentials);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this employee?")) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const filtered = list.filter((e) => {
    const q = search.toLowerCase();
    return !q || e.full_name.toLowerCase().includes(q) ||
      e.email.toLowerCase().includes(q) ||
      e.department?.toLowerCase().includes(q) ||
      e.designation?.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-2">
            Crew Manifest
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Employees</h1>
          <p className="text-sm text-zinc-500 mt-1">All onshore staff and seafarers in your fleet.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openCreate}
              data-testid="add-employee-btn"
              className="btn-pressable rounded-sm h-11 bg-[#F97316] hover:bg-[#EA580C] text-white font-mono uppercase tracking-widest text-xs px-5"
            >
              <Plus className="h-4 w-4 mr-2" /> Add Employee
            </Button>
          </DialogTrigger>
          <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm p-6">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl tracking-tight">
                {editing ? "Edit Employee" : "Add Employee"}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <Field label="Full name">
                <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="emp-name" className="rounded-sm" autoComplete="name" enterKeyHint="next" onKeyDown={(e) => { if (e.key === "Enter") 
                { emailRef.current?.focus();}
                }}/>
              </Field>
              <Field label="Email">
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-email" className="rounded-sm" autoComplete="email" enterKeyHint="next" onKeyDown={(e) => { if (e.key === "Enter") 
                { phoneRef.current?.focus(); }
                }} />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" autoComplete="tel" enterKeyHint="next"/>
              </Field>
              <Field label="Designation">
                <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="emp-designation" className="rounded-sm" />
              </Field>
              <Field label="Department">
                <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="emp-department"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Operations", "Logistics", "Engineering", "Finance", "HR", "IT", "Sales", "Deck Crew", "Engine Crew"].map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Employee type">
                <Select value={form.employee_type} onValueChange={(v) => setForm({ ...form, employee_type: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="emp-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="onshore">Onshore</SelectItem>
                    <SelectItem value="seafarer">Seafarer</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Join date">
                <Input type="date" value={form.join_date} onChange={(e) => setForm({ ...form, join_date: e.target.value })} className="rounded-sm" data-testid="emp-join-date" />
              </Field>
              <Field label="Salary (monthly)">
                <Input type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} className="rounded-sm font-mono" data-testid="emp-salary" />
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="on_leave">On Leave</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Avatar URL (optional)">
                <Input value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} className="rounded-sm" />
              </Field>
              {!editing && (
                <div className="md:col-span-2 flex items-center gap-3 p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
                  <Checkbox id="send-email" checked={form.send_email} onCheckedChange={(v) => setForm({ ...form, send_email: !!v })} data-testid="emp-send-email" />
                  <Label htmlFor="send-email" className="text-xs text-zinc-700 cursor-pointer">
                    Also email login credentials to the employee
                    <span className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
                      (Email service not configured — credentials will be shown on screen for now)
                    </span>
                  </Label>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="emp-save" className="btn-pressable rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
                {saving ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm">
        <div className="px-4 md:px-6 py-3 border-b border-zinc-200 flex items-center gap-2">
          <Search className="h-4 w-4 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search employees, departments, designations…"
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-sm h-9 p-0 placeholder:text-zinc-400"
            data-testid="emp-search"
          />
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50 hover:bg-zinc-50">
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Employee</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Code</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Designation</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Dept</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Type</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-right">Salary</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Status</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-sm text-zinc-500 py-16">
                  <Users className="h-8 w-8 mx-auto mb-3 text-zinc-300" />
                  No employees yet. Click <span className="font-medium text-zinc-700">Add Employee</span> to begin.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((e) => (
              <TableRow key={e.id} className="hover:bg-zinc-50" data-testid={`emp-row-${e.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-sm grid place-items-center bg-zinc-100 font-heading font-bold text-xs">
                      {e.full_name.slice(0, 1).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{e.full_name}</div>
                      <div className="text-xs text-zinc-500">{e.email}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs">{e.employee_code}</TableCell>
                <TableCell className="text-sm">{e.designation}</TableCell>
                <TableCell className="text-sm">{e.department}</TableCell>
                <TableCell>
                  {e.employee_type === "seafarer" ? (
                    <Badge variant="outline" className="rounded-sm border-zinc-300 font-mono text-[10px] uppercase tracking-widest">
                      <Anchor className="h-3 w-3 mr-1" /> Seafarer
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-sm border-zinc-300 font-mono text-[10px] uppercase tracking-widest">
                      Onshore
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">{formatMoney(e.salary, "INR")}</TableCell>
                <TableCell>
                  <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm ${
                    e.status === "active" ? "bg-emerald-100 text-emerald-700" :
                    e.status === "on_leave" ? "bg-amber-100 text-amber-700" :
                    "bg-zinc-200 text-zinc-600"
                  }`}>
                    {e.status.replace("_", " ")}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(e)} data-testid={`emp-edit-${e.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(e.id)} data-testid={`emp-delete-${e.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <CredentialsModal open={!!creds} onClose={() => setCreds(null)} credentials={creds} />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">{label}</Label>
      {children}
    </div>
  );
}
